import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Plus, Store, LogOut, Eye, EyeOff, Copy, CheckCheck,
  Calendar, Search, Pencil, Trash2, PauseCircle, PlayCircle,
  ChefHat, KeyRound, X, AlertTriangle, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const saHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('superadmin_token')}`
});

// ─── Reusable Modal ───────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={onClose}>
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-7 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white">{title}</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

// ─── Password Field ───────────────────────────────────────────────
const PasswordInput = ({ value, onChange, placeholder = 'Min 6 characters', className = '' }) => {
  const [show, setShow] = useState(false);
  const base = "w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500";
  return (
    <div className="relative">
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} className={`${base} pr-11 ${className}`} />
      <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const inputClass = "w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500";
const labelClass = "block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5";

// ─── Main Component ───────────────────────────────────────────────
const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // Expanded restaurant detail
  const [expandedId, setExpandedId] = useState(null);

  // Modals
  const [showRegister, setShowRegister] = useState(false);
  const [showChangeOwnPw, setShowChangeOwnPw] = useState(false);
  const [editTarget, setEditTarget] = useState(null);         // restaurant obj
  const [deleteTarget, setDeleteTarget] = useState(null);     // restaurant obj
  const [resetAdminPwTarget, setResetAdminPwTarget] = useState(null); // restaurant obj
  const [resetChefPwTarget, setResetChefPwTarget] = useState(null);   // {restaurant, chef}

  // Forms
  const [registerForm, setRegisterForm] = useState({ name: '', admin_email: '', admin_password: '' });
  const [editForm, setEditForm] = useState({ name: '', admin_email: '' });
  const [ownPwForm, setOwnPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [newAdminPw, setNewAdminPw] = useState('');
  const [newChefPw, setNewChefPw] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('superadmin_token');
    if (!token) { navigate('/superadmin/login'); return; }
    fetchRestaurants();
  }, []);

  const handleUnauthorized = () => {
    toast.error('Session expired. Please login again.');
    localStorage.removeItem('superadmin_token');
    navigate('/superadmin/login');
  };

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/restaurants`, { headers: saHeaders() });
      setRestaurants(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) handleUnauthorized();
      else toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/superadmin/logout`, {}, { headers: saHeaders() });
    } catch {}
    localStorage.removeItem('superadmin_token');
    navigate('/superadmin/login');
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      toast.success('Restaurant ID copied');
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // ── Register Restaurant ──
  const handleRegister = async () => {
    if (!registerForm.name.trim()) return toast.error('Restaurant name required');
    if (!registerForm.admin_email.includes('@')) return toast.error('Valid email required');
    if (registerForm.admin_password.length < 6) return toast.error('Password min 6 characters');
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/restaurants/register`, registerForm, { headers: saHeaders() });
      toast.success(`"${registerForm.name}" registered!`);
      toast(`ID: ${res.data.restaurant_id}`, { description: 'Save this — used in QR codes', duration: 12000 });
      setRegisterForm({ name: '', admin_email: '', admin_password: '' });
      setShowRegister(false);
      fetchRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  // ── Edit Restaurant ──
  const handleEdit = async () => {
    if (!editForm.name.trim() && !editForm.admin_email.trim()) return toast.error('Nothing to update');
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/restaurants/${editTarget.id}`, editForm, { headers: saHeaders() });
      toast.success('Restaurant updated');
      setEditTarget(null);
      fetchRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSubmitting(false); }
  };

  // ── Suspend Toggle ──
  const handleSuspend = async (r) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/restaurants/${r.id}/suspend`, {}, { headers: saHeaders() });
      toast.success(res.data.message);
      fetchRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // ── Delete Restaurant ──
  const handleDelete = async () => {
    if (deleteConfirm !== deleteTarget.name) return toast.error('Name does not match');
    setSubmitting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/restaurants/${deleteTarget.id}`, { headers: saHeaders() });
      toast.success(`"${deleteTarget.name}" permanently deleted`);
      setDeleteTarget(null);
      setDeleteConfirm('');
      fetchRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally { setSubmitting(false); }
  };

  // ── Reset Admin Password ──
  const handleResetAdminPw = async () => {
    if (newAdminPw.length < 6) return toast.error('Min 6 characters');
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/restaurants/${resetAdminPwTarget.id}/reset-admin-password`, { new_password: newAdminPw }, { headers: saHeaders() });
      toast.success('Admin password reset');
      setResetAdminPwTarget(null);
      setNewAdminPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  // ── Reset Chef Password ──
  const handleResetChefPw = async () => {
    if (newChefPw.length < 6) return toast.error('Min 6 characters');
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/restaurants/${resetChefPwTarget.restaurant.id}/chefs/${resetChefPwTarget.chef.id}/reset-password`,
        { new_password: newChefPw },
        { headers: saHeaders() }
      );
      toast.success(`Password reset for ${resetChefPwTarget.chef.email}`);
      setResetChefPwTarget(null);
      setNewChefPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  // ── Delete Chef ──
  const handleDeleteChef = async (restaurant, chef) => {
    if (!window.confirm(`Remove chef ${chef.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/restaurants/${restaurant.id}/chefs/${chef.id}`, { headers: saHeaders() });
      toast.success('Chef removed');
      fetchRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // ── Change Own Password ──
  const handleChangeOwnPw = async () => {
    if (!ownPwForm.current_password || !ownPwForm.new_password) return toast.error('All fields required');
    if (ownPwForm.new_password.length < 8) return toast.error('New password min 8 characters');
    if (ownPwForm.new_password !== ownPwForm.confirm) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/superadmin/change-password`, {
        current_password: ownPwForm.current_password,
        new_password: ownPwForm.new_password
      }, { headers: saHeaders() });
      toast.success(res.data.message);
      setShowChangeOwnPw(false);
      // Force re-login since all sessions are killed
      setTimeout(() => {
        localStorage.removeItem('superadmin_token');
        navigate('/superadmin/login');
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  // ── Filtered list ──
  const filtered = restaurants.filter(r => {
    const q = search.toLowerCase();
    return !q || r.name?.toLowerCase().includes(q) || r.admin_email?.toLowerCase().includes(q);
  });

  const activeCount = restaurants.filter(r => !r.suspended).length;
  const suspendedCount = restaurants.filter(r => r.suspended).length;
  const thisMonth = restaurants.filter(r => {
    const d = new Date(r.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      {/* ── Header ── */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 border border-purple-500/20 rounded-xl">
              <ShieldCheck className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Super Admin Dashboard</h1>
              <p className="text-xs text-gray-500">Manage all restaurant accounts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowChangeOwnPw(true)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white px-3 py-2 rounded-lg hover:bg-gray-800 transition"
            >
              <Lock className="w-4 h-4" /> Change Password
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-red-400 px-3 py-2 rounded-lg hover:bg-gray-800 transition"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total</p>
            <p className="text-4xl font-bold text-white">{restaurants.length}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Active</p>
            <p className="text-4xl font-bold text-green-400">{activeCount}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Suspended</p>
            <p className="text-4xl font-bold text-yellow-400">{suspendedCount}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">This Month</p>
            <p className="text-4xl font-bold text-purple-400">{thisMonth}</p>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by restaurant name or email..."
              className="w-full bg-gray-900 border border-gray-700 text-white placeholder-gray-600 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <Button onClick={() => setShowRegister(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 h-11 rounded-xl whitespace-nowrap">
            <Plus className="w-4 h-4 mr-2" /> Register Restaurant
          </Button>
        </div>

        {/* ── Restaurant List ── */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-500 text-sm">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Store className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">{search ? `No results for "${search}"` : 'No restaurants yet'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filtered.map(r => {
                const isExpanded = expandedId === r.id;
                return (
                  <div key={r.id}>
                    {/* ── Row ── */}
                    <div className={`px-6 py-4 hover:bg-gray-800/40 transition ${r.suspended ? 'opacity-60' : ''}`}>
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${r.suspended ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-purple-500/10 border-purple-500/20'}`}>
                          <Store className={`w-5 h-5 ${r.suspended ? 'text-yellow-400' : 'text-purple-400'}`} />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-white">{r.name}</p>
                            {r.suspended && (
                              <span className="text-xs font-semibold bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-2 py-0.5 rounded-full">Suspended</span>
                            )}
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <ChefHat className="w-3 h-3" /> {r.chef_count || 0} chef{r.chef_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{r.admin_email}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <code className="text-xs text-gray-600 font-mono bg-gray-800 px-2 py-0.5 rounded truncate max-w-xs">{r.id}</code>
                            <button onClick={() => handleCopyId(r.id)} className="text-gray-600 hover:text-purple-400 transition flex-shrink-0">
                              {copiedId === r.id ? <CheckCheck className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>

                        {/* Date */}
                        <div className="text-right flex-shrink-0 hidden sm:block">
                          <div className="flex items-center gap-1 text-xs text-gray-600 justify-end">
                            <Calendar className="w-3 h-3" />
                            {r.created_at ? format(new Date(r.created_at), 'dd MMM yyyy') : '—'}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : r.id)}
                            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded-lg transition"
                          >
                            {isExpanded ? 'Close' : 'Details'}
                          </button>
                          <button onClick={() => { setEditTarget(r); setEditForm({ name: r.name, admin_email: r.admin_email }); }} className="p-2 text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition" title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleSuspend(r)} className={`p-2 rounded-lg transition ${r.suspended ? 'text-green-400 hover:bg-green-500/10' : 'text-yellow-400 hover:bg-yellow-500/10'}`} title={r.suspended ? 'Unsuspend' : 'Suspend'}>
                            {r.suspended ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                          </button>
                          <button onClick={() => { setResetAdminPwTarget(r); setNewAdminPw(''); }} className="p-2 text-gray-500 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition" title="Reset admin password">
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setDeleteTarget(r); setDeleteConfirm(''); }} className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition" title="Delete restaurant">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Expanded Detail ── */}
                    {isExpanded && (
                      <div className="bg-gray-800/50 border-t border-gray-800 px-6 py-5">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <ChefHat className="w-3.5 h-3.5" /> Chef Accounts
                        </p>
                        {!r.chefs || r.chefs.length === 0 ? (
                          <p className="text-sm text-gray-600 italic">No chef accounts created yet for this restaurant.</p>
                        ) : (
                          <div className="space-y-2">
                            {r.chefs.map(chef => (
                              <div key={chef.id} className="flex items-center justify-between bg-gray-900 border border-gray-700 rounded-xl px-4 py-3">
                                <div>
                                  <p className="text-sm font-medium text-white">{chef.email}</p>
                                  <p className="text-xs text-gray-500">
                                    Added {chef.created_at ? format(new Date(chef.created_at), 'dd MMM yyyy') : '—'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => { setResetChefPwTarget({ restaurant: r, chef }); setNewChefPw(''); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-purple-400 border border-purple-500/30 hover:bg-purple-500/10 rounded-lg transition"
                                  >
                                    <KeyRound className="w-3.5 h-3.5" /> Reset Password
                                  </button>
                                  <button
                                    onClick={() => handleDeleteChef(r, chef)}
                                    className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg transition"
                                    title="Delete chef"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════ */}

      {/* Register Restaurant */}
      {showRegister && (
        <Modal title="Register New Restaurant" onClose={() => setShowRegister(false)}>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Restaurant Name</label>
              <input type="text" value={registerForm.name} onChange={e => setRegisterForm({ ...registerForm, name: e.target.value })} placeholder="e.g. Sharma's Dhaba" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Admin Email</label>
              <input type="email" value={registerForm.admin_email} onChange={e => setRegisterForm({ ...registerForm, admin_email: e.target.value })} placeholder="admin@restaurant.com" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Admin Password</label>
              <PasswordInput value={registerForm.admin_password} onChange={e => setRegisterForm({ ...registerForm, admin_password: e.target.value })} />
              <p className="text-xs text-gray-600 mt-1">Share with the restaurant owner. They can change it after first login.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowRegister(false)} className="flex-1 h-11 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition">Cancel</button>
              <Button onClick={handleRegister} disabled={submitting} className="flex-1 h-11 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl">
                {submitting ? 'Registering...' : 'Register'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Restaurant */}
      {editTarget && (
        <Modal title={`Edit — ${editTarget.name}`} onClose={() => setEditTarget(null)}>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Restaurant Name</label>
              <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Admin Email</label>
              <input type="email" value={editForm.admin_email} onChange={e => setEditForm({ ...editForm, admin_email: e.target.value })} className={inputClass} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditTarget(null)} className="flex-1 h-11 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition">Cancel</button>
              <Button onClick={handleEdit} disabled={submitting} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Restaurant */}
      {deleteTarget && (
        <Modal title="Delete Restaurant" onClose={() => setDeleteTarget(null)}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-400">This cannot be undone</p>
                <p className="text-xs text-red-400/70 mt-1">This will permanently delete <strong>{deleteTarget.name}</strong> and ALL its data — menu, orders, billing, customers, everything.</p>
              </div>
            </div>
            <div>
              <label className={labelClass}>Type <span className="text-red-400 normal-case font-mono">{deleteTarget.name}</span> to confirm</label>
              <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder={deleteTarget.name} className={inputClass} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 h-11 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition">Cancel</button>
              <Button onClick={handleDelete} disabled={submitting || deleteConfirm !== deleteTarget.name} className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl disabled:opacity-40">
                {submitting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Admin Password */}
      {resetAdminPwTarget && (
        <Modal title={`Reset Admin Password — ${resetAdminPwTarget.name}`} onClose={() => setResetAdminPwTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Set a new password for the admin account of this restaurant. Share it with the owner.</p>
            <div>
              <label className={labelClass}>New Password</label>
              <PasswordInput value={newAdminPw} onChange={e => setNewAdminPw(e.target.value)} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setResetAdminPwTarget(null)} className="flex-1 h-11 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition">Cancel</button>
              <Button onClick={handleResetAdminPw} disabled={submitting} className="flex-1 h-11 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl">
                {submitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Chef Password */}
      {resetChefPwTarget && (
        <Modal title={`Reset Chef Password`} onClose={() => setResetChefPwTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Chef: <span className="text-white font-medium">{resetChefPwTarget.chef.email}</span></p>
            <div>
              <label className={labelClass}>New Password</label>
              <PasswordInput value={newChefPw} onChange={e => setNewChefPw(e.target.value)} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setResetChefPwTarget(null)} className="flex-1 h-11 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition">Cancel</button>
              <Button onClick={handleResetChefPw} disabled={submitting} className="flex-1 h-11 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl">
                {submitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Change Own Password */}
      {showChangeOwnPw && (
        <Modal title="Change Super Admin Password" onClose={() => setShowChangeOwnPw(false)}>
          <div className="space-y-4">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-xs text-yellow-400">
              After changing your password you will be logged out and need to login again.
            </div>
            <div>
              <label className={labelClass}>Current Password</label>
              <PasswordInput value={ownPwForm.current_password} onChange={e => setOwnPwForm({ ...ownPwForm, current_password: e.target.value })} placeholder="Current password" />
            </div>
            <div>
              <label className={labelClass}>New Password</label>
              <PasswordInput value={ownPwForm.new_password} onChange={e => setOwnPwForm({ ...ownPwForm, new_password: e.target.value })} placeholder="Min 8 characters" />
            </div>
            <div>
              <label className={labelClass}>Confirm New Password</label>
              <PasswordInput value={ownPwForm.confirm} onChange={e => setOwnPwForm({ ...ownPwForm, confirm: e.target.value })} placeholder="Re-enter new password" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowChangeOwnPw(false)} className="flex-1 h-11 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition">Cancel</button>
              <Button onClick={handleChangeOwnPw} disabled={submitting} className="flex-1 h-11 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl">
                {submitting ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default SuperAdminDashboard;
